import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faBuilding,
  faCircleCheck,
  faKeyboard,
  faFileImport,
  faInbox,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import { useSupplierInbox } from 'modules/FreeSupplier/useSupplierInbox';
import {
  findSupplierInboxRequest,
  formatRequestInterval,
  formatState,
} from 'modules/FreeSupplier/supplierInboxPresentation';
import {
  SupplierRoutePath,
  buildSupplierRequestDirectEntryPath,
  buildSupplierRequestUploadPath,
} from 'modules/FreeSupplier/routes';
import { SupplierPanelLoader } from '../components/SupplierLoadingStates';

// The request-tailored surface (/supplier/requests/:requestId). Reads the request
// from the S3 inbox, shows WHO asked, WHICH CN codes, WHICH reporting period, and
// whether published data already covers each line — then offers the two fastest
// actions, both preselecting the request's CN code + interval. Per D10 there is
// no accept-all: each request is fulfilled on its own.
export default function RequestTailoredPage() {
  const { requestId } = useParams();
  const history = useHistory();
  const inbox = useSupplierInbox();
  const connections = inbox.data?.items || [];
  const match = findSupplierInboxRequest(connections, requestId);

  if (inbox.isPending) {
    return (
      <SupplierPanelLoader
        ariaLabel="Loading this data request"
        messages={[
          'Opening the data request',
          'Checking who asked and for which goods',
          'Matching it against your published data',
        ]}
      />
    );
  }

  // Item 8: a query ERROR is not "not found" — a retryable error state, not
  // the permanent-looking "we couldn't find that request" copy below.
  if (inbox.isError) {
    return (
      <div className="rounded-2xl border border-dashed border-red-300 p-10 text-center">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="text-red-300 text-2xl mb-3"
        />
        <p className="text-sm text-slate-600 m-0">We couldn’t load this request.</p>
        <p className="text-xs text-slate-400 mt-1 m-0">Check your connection and try again.</p>
        <CgRadixButton
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={() => inbox.refetch()}
        >
          Retry
        </CgRadixButton>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
        <FontAwesomeIcon
          icon={faInbox}
          className="text-slate-300 text-2xl mb-3"
        />
        <p className="text-sm text-slate-600 m-0">We couldn’t find that request.</p>
        <p className="text-xs text-slate-400 mt-1 m-0">
          It may have been withdrawn, or belongs to a different workspace.
        </p>
        <Link
          to={SupplierRoutePath.Inbox}
          className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-primaryColor no-underline"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Data Requests
        </Link>
      </div>
    );
  }

  const { connection, request } = match;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to={SupplierRoutePath.Inbox}
          className="text-xs text-slate-400 no-underline hover:text-slate-600 inline-flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Data Requests
        </Link>
        <h1 className="text-xl font-bold text-slate-800 m-0 mt-1">Answer this data request</h1>
        <p className="text-sm text-slate-500 mt-1 m-0">
          Share exactly the goods and reporting period this customer asked for. Nothing else leaves
          your workspace.
        </p>
      </div>

      {/* Item B1 (product feedback): the header above stays flush-left/full-width
          like every other page — only the body below picks a comfortable,
          centered reading width (it's a single-column detail surface). */}
      <div className="flex flex-col gap-6 max-w-3xl w-full mx-auto">
        {/* Who asked */}
        <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {/* Q4 (S7 fix batch 3): `flex-wrap` lets the state chip drop to its own
            line instead of overflowing past the row (and the page body) when
            the row is too narrow to hold icon + name/period + chip on one
            line — a `shrink-0` chip alongside a `min-w-0` sibling doesn't
            shrink on its own, it just spills out horizontally. `break-words`
            on the name is the same belt-and-braces for a long, unbroken
            declarant name. */}
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 bg-slate-50/60 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faBuilding}
                className="text-slate-400"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 m-0 break-words">
                {connection.declarant.name}
              </p>
              <p className="text-xs text-slate-400 m-0 mt-0.5">
                Reporting period · {request.reportingPeriod}
              </p>
            </div>
            <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 text-amber-700 bg-amber-50 shrink-0">
              {formatState(request.state)}
            </span>
          </div>

          <ul className="list-none p-0 m-0 divide-y divide-slate-100">
            {request.lines.map((line, idx) => {
              const covered = line.fulfilment?.state === 'fulfilled';
              return (
                <li
                  key={`${line.cnCode}-${idx}`}
                  className="flex flex-wrap items-center gap-3 px-5 py-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 m-0">
                      <CgCnGoodHoverText
                        cnCode={line.cnCode}
                        useAutonomousData
                        variant="plain"
                      />
                    </p>
                    <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                      {formatRequestInterval(line)}
                    </p>
                  </div>
                  {covered ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 shrink-0">
                      <FontAwesomeIcon icon={faCircleCheck} />
                      Covered by published data
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 shrink-0">
                      Not covered yet
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* Fastest actions — both preselect the CN code + interval. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => history.push(buildSupplierRequestDirectEntryPath(requestId))}
            className="text-left rounded-2xl bg-white border border-slate-200 p-5 hover:border-primaryColor/40 hover:shadow-sm transition-all"
          >
            <div className="w-9 h-9 rounded-xl bg-primaryColor/10 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faKeyboard}
                className="text-primaryColor"
              />
            </div>
            <p className="text-sm font-semibold text-slate-800 m-0">Enter the data directly</p>
            <p className="text-xs text-slate-500 mt-1 m-0">
              Fill in direct and indirect emissions for the requested good — pre-filled with the CN
              code and period.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-primaryColor">
              Enter directly{' '}
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-[10px]"
              />
            </span>
          </button>

          <button
            type="button"
            onClick={() => history.push(buildSupplierRequestUploadPath(requestId))}
            className="text-left rounded-2xl bg-white border border-slate-200 p-5 hover:border-primaryColor/40 hover:shadow-sm transition-all"
          >
            <div className="w-9 h-9 rounded-xl bg-primaryColor/10 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faFileImport}
                className="text-primaryColor"
              />
            </div>
            <p className="text-sm font-semibold text-slate-800 m-0">Upload your template</p>
            <p className="text-xs text-slate-500 mt-1 m-0">
              Already filled in the EU communication template? Drop it in and we’ll extract the
              emission factors.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-primaryColor">
              Upload template{' '}
              <FontAwesomeIcon
                icon={faArrowRight}
                className="text-[10px]"
              />
            </span>
          </button>
        </div>

        <CgRadixButton
          variant="ghost"
          size="sm"
          onClick={() => history.push(SupplierRoutePath.Inbox)}
        >
          Back to all requests
        </CgRadixButton>
      </div>
    </div>
  );
}
